/**
 * 性能测试和分析命令
 */

import { Command } from 'commander';
import { runPerformanceTests } from '../tests/performance/performance-test-suite';
import { performanceMonitor } from '../core/performance-monitor';
import { performanceOptimizer } from '../core/performance-optimizer';
import { logger } from '../utils/logger';
import chalk from 'chalk';
import Table from 'cli-table3';
import * as fs from 'fs-extra';
import * as path from 'path';
import inquirer from 'inquirer';

export function createPerformanceCommand(): Command {
  const command = new Command('performance')
    .description('性能测试和优化工具')
    .alias('perf');
  
  // 运行性能测试
  command
    .command('test')
    .description('运行完整的性能测试套件')
    .option('-o, --output <path>', '报告输出路径', './performance-report.json')
    .option('-s, --scenario <name>', '只运行特定场景')
    .option('--no-warmup', '跳过预热')
    .action(async (options) => {
      console.log(chalk.blue('🚀 启动性能测试...\n'));
      
      try {
        await runPerformanceTests();
        
        console.log(chalk.green('\n✅ 性能测试完成'));
        console.log(chalk.dim(`报告已保存到: ${options.output}`));
      } catch (error) {
        console.error(chalk.red('性能测试失败:'), error);
        process.exit(1);
      }
    });
  
  // 分析性能报告
  command
    .command('analyze [report]')
    .description('分析性能报告')
    .option('-t, --threshold <ms>', '慢操作阈值（毫秒）', '500')
    .action(async (reportPath = './performance-report.json', options) => {
      try {
        if (!await fs.pathExists(reportPath)) {
          console.error(chalk.red(`报告文件不存在: ${reportPath}`));
          return;
        }
        
        const report = await fs.readJson(reportPath);
        analyzeReport(report, parseInt(options.threshold));
      } catch (error) {
        console.error(chalk.red('分析失败:'), error);
      }
    });
  
  // 实时监控
  command
    .command('monitor')
    .description('实时监控性能')
    .option('-d, --duration <seconds>', '监控时长（秒）', '60')
    .action(async (options) => {
      const duration = parseInt(options.duration) * 1000;
      
      console.log(chalk.blue(`📊 开始性能监控 (${options.duration}秒)...\n`));
      
      // 设置监控事件
      performanceMonitor.on('operation:complete', (metrics) => {
        const color = metrics.duration > 500 ? chalk.red : 
                     metrics.duration > 200 ? chalk.yellow : 
                     chalk.green;
        
        console.log(
          `${color('●')} ${metrics.operation}: ${metrics.duration.toFixed(2)}ms`
        );
      });
      
      performanceMonitor.on('performance:issue', ({ metrics, issues }) => {
        console.log(chalk.red('\n⚠️  性能问题检测到:'));
        issues.forEach(issue => console.log(`   - ${issue}`));
      });
      
      // 等待指定时间
      await new Promise(resolve => setTimeout(resolve, duration));
      
      // 生成报告
      const report = performanceMonitor.generateReport();
      console.log(chalk.blue('\n📊 监控报告:'));
      printMonitoringSummary(report);
    });
  
  // 优化建议
  command
    .command('optimize')
    .description('获取性能优化建议')
    .action(async () => {
      const suggestions = await generateOptimizationSuggestions();
      
      console.log(chalk.blue('🎯 性能优化建议:\n'));
      
      suggestions.forEach((suggestion, index) => {
        console.log(chalk.bold(`${index + 1}. ${suggestion.title}`));
        console.log(chalk.dim(`   问题: ${suggestion.issue}`));
        console.log(`   建议: ${suggestion.recommendation}`);
        if (suggestion.example) {
          console.log(chalk.cyan(`   示例:\n${suggestion.example}`));
        }
        console.log();
      });
    });
  
  // 缓存统计
  command
    .command('cache')
    .description('查看缓存统计信息')
    .action(() => {
      const caches = performanceOptimizer['caches'];
      
      if (caches.size === 0) {
        console.log(chalk.yellow('没有活跃的缓存'));
        return;
      }
      
      const table = new Table({
        head: ['缓存名称', '大小', '命中率', '最大容量'],
        colWidths: [20, 10, 15, 15]
      });
      
      caches.forEach((cache, name) => {
        const stats = cache.dump();
        const size = stats.length;
        const maxSize = cache.max;
        const hitRate = '计算中...'; // 需要额外跟踪
        
        table.push([name, size, hitRate, maxSize]);
      });
      
      console.log(chalk.blue('📦 缓存统计:\n'));
      console.log(table.toString());
    });
  
  // 基准测试
  command
    .command('benchmark <operation>')
    .description('对特定操作进行基准测试')
    .option('-n, --iterations <count>', '迭代次数', '100')
    .action(async (operation, options) => {
      const iterations = parseInt(options.iterations);
      
      console.log(chalk.blue(`🏃 运行基准测试: ${operation} (${iterations} 次迭代)\n`));
      
      const operations = {
        'task-create': async () => {
          // 模拟任务创建
          const data = { content: 'Benchmark task', priority: 'medium' };
          await new Promise(resolve => setTimeout(resolve, Math.random() * 10));
          return data;
        },
        'file-read': async () => {
          // 模拟文件读取
          const content = await fs.readFile(__filename, 'utf-8');
          return content.length;
        },
        'json-parse': () => {
          // 模拟 JSON 解析
          const data = JSON.stringify({ test: true, items: Array(100).fill(0) });
          return JSON.parse(data);
        }
      };
      
      if (!operations[operation]) {
        console.error(chalk.red(`未知操作: ${operation}`));
        console.log('可用操作:', Object.keys(operations).join(', '));
        return;
      }
      
      // 预热
      console.log(chalk.dim('预热中...'));
      for (let i = 0; i < 10; i++) {
        await operations[operation]();
      }
      
      // 基准测试
      console.log(chalk.dim('测试中...'));
      const results: number[] = [];
      
      for (let i = 0; i < iterations; i++) {
        const start = performance.now();
        await operations[operation]();
        const end = performance.now();
        results.push(end - start);
        
        if (i % 10 === 0) {
          process.stdout.write('.');
        }
      }
      
      console.log('\n');
      
      // 分析结果
      const stats = calculateStats(results);
      
      console.log(chalk.blue('📊 基准测试结果:\n'));
      console.log(`操作: ${operation}`);
      console.log(`迭代: ${iterations} 次`);
      console.log(`平均: ${stats.mean.toFixed(2)}ms`);
      console.log(`中位数: ${stats.median.toFixed(2)}ms`);
      console.log(`最小: ${stats.min.toFixed(2)}ms`);
      console.log(`最大: ${stats.max.toFixed(2)}ms`);
      console.log(`标准差: ${stats.stdDev.toFixed(2)}ms`);
      console.log(`P95: ${stats.p95.toFixed(2)}ms`);
      console.log(`P99: ${stats.p99.toFixed(2)}ms`);
    });
  
  return command;
}

/**
 * 分析报告
 */
function analyzeReport(report: any, threshold: number): void {
  console.log(chalk.blue('📊 性能分析报告\n'));
  
  // 系统信息
  console.log(chalk.bold('系统信息:'));
  console.log(`- 平台: ${report.systemInfo.platform}`);
  console.log(`- CPU: ${report.systemInfo.cpus} 核`);
  console.log(`- 内存: ${(report.systemInfo.totalMemory / 1024 / 1024 / 1024).toFixed(2)} GB`);
  console.log();
  
  // 性能摘要
  console.log(chalk.bold('性能摘要:'));
  console.log(`- 总操作数: ${report.summary.totalOperations}`);
  console.log(`- 总耗时: ${report.summary.totalDuration.toFixed(2)}ms`);
  console.log(`- 平均耗时: ${report.summary.averageDuration.toFixed(2)}ms`);
  console.log();
  
  // 慢操作
  const slowOps = report.metrics.filter(m => m.duration > threshold);
  if (slowOps.length > 0) {
    console.log(chalk.bold(`慢操作 (>${threshold}ms):`));
    const table = new Table({
      head: ['操作', '耗时(ms)', '内存(MB)', 'CPU(%)'],
      colWidths: [30, 15, 15, 15]
    });
    
    slowOps
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10)
      .forEach(op => {
        table.push([
          op.operation,
          op.duration.toFixed(2),
          (op.memoryUsage.heapUsed / 1024 / 1024).toFixed(2),
          ((op.cpuUsage.user + op.cpuUsage.system) / 1000000 * 100).toFixed(2)
        ]);
      });
    
    console.log(table.toString());
    console.log();
  }
  
  // 建议
  if (report.recommendations.length > 0) {
    console.log(chalk.bold('优化建议:'));
    report.recommendations.forEach((rec, index) => {
      console.log(`${index + 1}. ${rec}`);
    });
  }
}

/**
 * 打印监控摘要
 */
function printMonitoringSummary(report: any): void {
  if (report.metrics.length === 0) {
    console.log(chalk.yellow('没有捕获到操作'));
    return;
  }
  
  const table = new Table({
    head: ['操作', '次数', '平均耗时', '最小/最大'],
    colWidths: [25, 10, 15, 20]
  });
  
  // 按操作分组
  const grouped = report.metrics.reduce((acc, metric) => {
    if (!acc[metric.operation]) {
      acc[metric.operation] = [];
    }
    acc[metric.operation].push(metric);
    return acc;
  }, {});
  
  Object.entries(grouped).forEach(([op, metrics]: [string, any[]]) => {
    const durations = metrics.map(m => m.duration);
    const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
    const min = Math.min(...durations);
    const max = Math.max(...durations);
    
    table.push([
      op,
      metrics.length,
      `${avg.toFixed(2)}ms`,
      `${min.toFixed(2)}/${max.toFixed(2)}ms`
    ]);
  });
  
  console.log(table.toString());
}

/**
 * 生成优化建议
 */
async function generateOptimizationSuggestions(): Promise<any[]> {
  const suggestions = [
    {
      title: '使用缓存减少重复计算',
      issue: '相同的操作被多次执行',
      recommendation: '对计算密集型操作使用 memoization',
      example: `
import { cache } from '@gongfu/workflow-cli';

const expensiveOperation = cache(
  (input: string) => {
    // 昂贵的计算
    return result;
  },
  { cache: 'my-cache', ttl: 60000 }
);`
    },
    {
      title: '批量处理减少 I/O',
      issue: '频繁的小文件读写操作',
      recommendation: '使用批处理器合并操作',
      example: `
const batchProcessor = performanceOptimizer.createBatchProcessor(
  'file-writes',
  async (items) => {
    await fs.writeJson('batch.json', items);
  },
  { maxSize: 100, maxWait: 1000 }
);`
    },
    {
      title: '并发控制',
      issue: '过多的并发操作导致资源竞争',
      recommendation: '使用并发限制器',
      example: `
const limiter = performanceOptimizer.createLimiter('api-calls', 5);

await Promise.all(
  urls.map(url => limiter(() => fetch(url)))
);`
    },
    {
      title: '防抖和节流',
      issue: '高频触发的操作',
      recommendation: '对用户输入和频繁操作使用防抖/节流',
      example: `
const debouncedSave = performanceOptimizer.debounce(
  saveFunction,
  300
);`
    },
    {
      title: '懒加载',
      issue: '启动时加载过多模块',
      recommendation: '使用懒加载延迟初始化',
      example: `
const getHeavyModule = performanceOptimizer.lazy(
  () => require('./heavy-module')
);`
    }
  ];
  
  return suggestions;
}

/**
 * 计算统计信息
 */
function calculateStats(values: number[]): any {
  const sorted = [...values].sort((a, b) => a - b);
  const sum = values.reduce((a, b) => a + b, 0);
  const mean = sum / values.length;
  
  const variance = values.reduce((acc, val) => {
    return acc + Math.pow(val - mean, 2);
  }, 0) / values.length;
  
  return {
    mean,
    median: sorted[Math.floor(sorted.length / 2)],
    min: sorted[0],
    max: sorted[sorted.length - 1],
    stdDev: Math.sqrt(variance),
    p95: sorted[Math.floor(sorted.length * 0.95)],
    p99: sorted[Math.floor(sorted.length * 0.99)]
  };
}